import React, { useMemo, useState } from 'react';
import { Box, Typography, Button, TextField, InputAdornment, IconButton } from '@mui/material';
import {
  LocalHospital as HospitalIcon,
  Business as BureauIcon,
  CheckCircle as CheckIcon,
  Search as SearchIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { BRAND, brandFieldSx } from '../../../brand';
import { getAllDepartments } from '../../data/department';

// ============================================================================
//  InstallStep — what kind of installation is this?
// ============================================================================
//
//  Asked once, at setup, because it decides which tables hold the claims for the
//  life of the installation:
//
//    facility  a hospital running MEFIMS for its own patients. Claims are typed
//              in by its own staff and live in the flat claim_information table.
//
//    bureau    Gigatt Apex doing claims work FOR other hospitals. No patients of
//              its own; claims arrive as NHIA e-Claims XML batches per client
//              facility and live in the normalised claims tables.
//
//  Before this step existed, both sets of tables were created on every install
//  and nothing recorded which one was in use — so an imported batch was
//  invisible to the very screens meant to work on it.
//
//  Changeable later from System Settings, but not casually: switching mode after
//  claims exist points the screens at a different set of tables. Said plainly on
//  screen rather than left to be discovered.

const MODES = [
  {
    id: 'facility',
    icon: HospitalIcon,
    title: 'Hospital / Facility',
    tagline: 'This hospital, running its own operations',
    points: [
      'Your own patients, staff and departments',
      'Claims are entered by your staff as care is delivered',
      'Wards, pharmacy, laboratory and the full clinical set',
    ],
    // The departments a hospital gets by default: everything except the
    // vendor-side onboarding screens.
    defaultDepartments: 'all',
  },
  {
    id: 'bureau',
    icon: BureauIcon,
    title: 'Claims Bureau',
    tagline: 'Gigatt Apex, doing claims work for other facilities',
    points: [
      'No patients of its own — you work on other hospitals\' claims',
      'Facilities are onboarded, then their NHIA XML batches imported',
      'Claims, Templates and the IT / ICT tools only',
    ],
    defaultDepartments: [
      'Hospital Claims',
      'IT / ICT Department',
      'Health Information',
    ],
  },
];

export default function InstallStep({ data, onChange, error }) {
  const mode = data?.mode || 'facility';
  const chosen = Array.isArray(data?.departments) ? data.departments : null;
  const allDepartments = getAllDepartments();

  // Filter text only. It narrows what is DISPLAYED and never what is selected —
  // so typing into it can't silently drop a department from the install.
  const [deptFilter, setDeptFilter] = useState('');

  const visibleDepartments = useMemo(() => {
    const q = deptFilter.trim().toLowerCase();
    if (!q) return allDepartments;
    // Matches the description too, so "drug" finds Pharmacy — which is how
    // someone who does not know a department's formal name will look for it.
    return allDepartments.filter(d =>
      `${d.name} ${d.description || ''}`.toLowerCase().includes(q)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deptFilter]);

  const pickMode = (id) => {
    const preset = MODES.find((m) => m.id === id);
    onChange('mode', id);
    // Reset the department list to the preset for the newly chosen mode.
    // Carrying a bureau's three departments into a facility install would leave
    // a hospital with no wards, and the reverse is equally wrong.
    onChange('departments', preset.defaultDepartments === 'all' ? null : preset.defaultDepartments);
  };

  const toggleDepartment = (name) => {
    const current = chosen || allDepartments.map((d) => d.name);
    const next = current.includes(name)
      ? current.filter((n) => n !== name)
      : [...current, name];
    // Everything selected is the same thing as no restriction; store it as such
    // so a later department added to the catalogue is not silently excluded.
    onChange('departments', next.length === allDepartments.length ? null : next);
  };

  const isOn = (name) => !chosen || chosen.includes(name);

  return (
    <Box>
      <Typography sx={{ fontSize: 13.5, color: BRAND.inkMuted, mb: 2.5, lineHeight: 1.7, maxWidth: '62ch' }}>
        This decides where claims are stored and which departments appear. It can be
        changed later in System Settings, but not lightly — switching after claims exist
        points the screens at a different set of tables.
      </Typography>

      {/* ── Mode ─────────────────────────────────────────────────────────── */}
      <Box sx={{
        display: 'grid', gap: 1.75, mb: 3.5,
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
      }}>
        {MODES.map((m) => {
          const on = mode === m.id;
          const Icon = m.icon;
          return (
            <Box
              key={m.id}
              component="button"
              type="button"
              onClick={() => pickMode(m.id)}
              aria-pressed={on}
              sx={{
                textAlign: 'left', cursor: 'pointer', font: 'inherit',
                p: 2.25, borderRadius: '14px',
                border: `1.5px solid ${on ? BRAND.green : BRAND.line}`,
                background: on ? BRAND.greenSoft : BRAND.surface,
                transition: 'border-color .15s ease, background .15s ease',
                '&:hover': { borderColor: on ? BRAND.greenDark : BRAND.navyLight },
                '&:focus-visible': { outline: `2px solid ${BRAND.navy}`, outlineOffset: 2 },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
                <Box sx={{
                  width: 38, height: 38, borderRadius: '10px', flexShrink: 0,
                  display: 'grid', placeItems: 'center',
                  background: on ? BRAND.green : BRAND.navySoft,
                  color: on ? '#fff' : BRAND.navy,
                }}>
                  <Icon sx={{ fontSize: 20 }} />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: 15, fontWeight: 750, color: BRAND.ink, lineHeight: 1.3 }}>
                    {m.title}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: BRAND.inkMuted }}>
                    {m.tagline}
                  </Typography>
                </Box>
                {on && <CheckIcon sx={{ fontSize: 20, color: BRAND.green, flexShrink: 0 }} />}
              </Box>

              <Box component="ul" sx={{ m: 0, pl: 2.25, display: 'grid', gap: .5 }}>
                {m.points.map((p) => (
                  <Typography key={p} component="li"
                    sx={{ fontSize: 12.5, color: BRAND.inkMuted, lineHeight: 1.55 }}>
                    {p}
                  </Typography>
                ))}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* ── Departments ──────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Typography sx={{
          fontSize: 11, fontWeight: 800, letterSpacing: '.9px', textTransform: 'uppercase',
          color: BRAND.inkFaint,
        }}>
          Departments on this installation
        </Typography>

        <Box sx={{ flexGrow: 1 }} />

        {/* Select all / Clear all.
            Thirty-five departments in a scrolling box meant clicking one after
            another to get to a short list — and the common case for a bureau or
            a clinic is "none of them, then pick four", which was thirty-five
            clicks the wrong way round.

            Clear all leaves an EMPTY list rather than falling back to "all", so
            it is a real starting point to build from. `[]` is meaningful here:
            null means no restriction, [] means nothing chosen yet. */}
        <Button
          size="small"
          onClick={() => onChange('departments', null)}
          disabled={!chosen}
          sx={{
            textTransform: 'none', fontSize: 12, fontWeight: 700, minWidth: 0, px: 1,
            color: BRAND.navy,
            '&:hover': { background: BRAND.navySoft },
            '&.Mui-disabled': { color: BRAND.inkFaint },
          }}
        >
          Select all
        </Button>
        <Button
          size="small"
          onClick={() => onChange('departments', [])}
          disabled={chosen?.length === 0}
          sx={{
            textTransform: 'none', fontSize: 12, fontWeight: 700, minWidth: 0, px: 1,
            color: BRAND.inkMuted,
            '&:hover': { background: BRAND.canvas },
            '&.Mui-disabled': { color: BRAND.inkFaint },
          }}
        >
          Clear all
        </Button>
      </Box>

      <Typography sx={{
        fontSize: 12.5, mb: 1.5, maxWidth: '62ch', lineHeight: 1.6,
        color: error ? BRAND.danger : BRAND.inkMuted,
        fontWeight: error ? 600 : 400,
      }}>
        {error
          ? error
          : !chosen
            ? `All ${allDepartments.length} selected. Turn off any this facility does not run.`
            : chosen.length === 0
              ? 'None selected — pick the departments this installation runs.'
              : `${chosen.length} of ${allDepartments.length} selected. Only these will appear on the dashboard.`}
      </Typography>

      {/* Filter. Finding "Procurement & Supplies" in a 35-item box you can only
          see nine of at a time is its own small chore; typing three letters is
          faster than scrolling. Filtering never changes what is selected. */}
      <TextField
        fullWidth
        size="small"
        placeholder="Filter departments…"
        value={deptFilter}
        onChange={(e) => setDeptFilter(e.target.value)}
        sx={{ ...brandFieldSx, mb: 1.25 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 17, color: BRAND.inkFaint }} />
            </InputAdornment>
          ),
          endAdornment: deptFilter ? (
            <InputAdornment position="end">
              <IconButton size="small" aria-label="Clear filter" onClick={() => setDeptFilter('')}>
                <CloseIcon sx={{ fontSize: 15, color: BRAND.inkFaint }} />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />

      <Box sx={{
        display: 'grid', gap: .75,
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0,1fr))', lg: 'repeat(3, minmax(0,1fr))' },
        maxHeight: 260, overflowY: 'auto',
        p: 1.25, borderRadius: '12px',
        border: `1px solid ${BRAND.line}`, background: BRAND.canvas,
      }}>
        {visibleDepartments.length === 0 && (
          <Typography sx={{ gridColumn: '1 / -1', fontSize: 12.5, color: BRAND.inkMuted, textAlign: 'center', py: 2 }}>
            Nothing matches “{deptFilter.trim()}”.
          </Typography>
        )}
        {visibleDepartments.map((d) => {
          const on = isOn(d.name);
          return (
            <Box
              key={d.id}
              component="button"
              type="button"
              onClick={() => toggleDepartment(d.name)}
              aria-pressed={on}
              title={d.description}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1, minWidth: 0,
                textAlign: 'left', cursor: 'pointer', font: 'inherit',
                px: 1.25, py: .875, borderRadius: '9px',
                border: `1px solid ${on ? BRAND.green : BRAND.line}`,
                background: on ? BRAND.surface : 'transparent',
                '&:hover': { borderColor: on ? BRAND.greenDark : BRAND.navyLight },
                '&:focus-visible': { outline: `2px solid ${BRAND.navy}`, outlineOffset: 1 },
              }}
            >
              <Box sx={{
                width: 16, height: 16, borderRadius: '4px', flexShrink: 0,
                display: 'grid', placeItems: 'center',
                background: on ? BRAND.green : 'transparent',
                border: `1.5px solid ${on ? BRAND.green : BRAND.inkFaint}`,
              }}>
                {on && <CheckIcon sx={{ fontSize: 12, color: '#fff' }} />}
              </Box>
              <Typography sx={{
                fontSize: 12.5, minWidth: 0,
                fontWeight: on ? 650 : 500,
                color: on ? BRAND.ink : BRAND.inkFaint,
              }} noWrap>
                {d.name}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
